<template>
  <div class="configuration-sidepanel px-4">
    <b-row class="d-flex align-items-center mb-2">
      <b-col class="d-flex align-items-center">
        <span class="ap-title ap-tittle__main"><i
          class="mr-2"
          :class="icon"
          aria-hidden="true"
        />{{ sidepanelTitle }}</span>
      </b-col>
      <b-col
        cols="auto"
        class="d-flex justify-content-end"
      >
        <div>
          <b-button
            v-shortkey="['shift', 'esc']"
            class="btn__icon btn__icon--primary p-0"
            @shortkey="closeConfigurationSidepanel"
            @click="closeConfigurationSidepanel"
          >
            <i
              class="far fa-times"
              aria-hidden="true"
            />
          </b-button>
        </div>
      </b-col>
    </b-row>
    <configuration-sidepanel-tabs
      v-if="hasTabs"
      :show-center-filter="showCenterFilter"
      :show-operation-group-filter="showOperationGroupFilter"
      :associations-list="associationsList"
      :association-types="associationTypes"
      @update="$emit('update', $event)"
    />
    <div v-if="!hasTabs">
      <b-row class="my-2">
        <b-col
          v-if="showCenterFilter && laboratories.length"
          cols="12"
        >
          <div class="form-label">
            <label>{{ $tc('configuration.grid.center', 1) }}</label>
          </div>
          <vt-combo-select
            v-model="labSelected"
            track-by="labId"
            :options="laboratories"
            label="labName"
          />
        </b-col>
        <!-- TODO: Pendiente de enlazar correctamente el combo de asociación de entidades en el sidepanel -->
        <b-col
          v-if="showEntitiesAssociationFilter"
          cols="12"
        >
          <div class="form-label">
            <label>{{ $t('configuration.sections.entitiesAssociation') }}</label>
          </div>
          <vt-combo-select
            rack-by="labId"
            label="labName"
          />
        </b-col>
        <b-col class="d-flex align-items-center mt-3">
          <span class="dark">{{ `${internalList.length} ${$tc(association, internalList.length)}` }}</span>
          <b-button
            v-if="!editDisabled"
            v-tooltip="$t('configuration.actions.update')"
            class="btn__icon btn__icon--primary  pl-3"
            @click="$emit('update', associationType)"
          >
            <i
              class="far fa-edit"
              aria-hidden="true"
            />
          </b-button>
        </b-col>
        <operation-group-filter
          v-if="showOperationGroupFilter && associationType === ASSOCIATION_TYPE.OPERATION.key"
          @change="groupFilter = $event"
        />
      </b-row>
      <div class="px-3 scroll-area mt-4">
        <b-row
          v-for="(associationFiltered, index) in filteredInternalList"
          :key="index"
          class="tech-row bottom-bordered py-2"
        >
          <b-col class="label-dark">
            {{ getAssociationName(associationFiltered[associationType]) }}
            <i
              v-if="showOperationTooltip && associationType === ASSOCIATION_TYPE.OPERATION.key && getOperationAssociatedRoles(associationFiltered[ASSOCIATION_TYPE.OPERATION.key]) !== ''"
              v-tooltip="`${$t('configuration.modal.comeFromRole')}:${getOperationAssociatedRoles(associationFiltered[ASSOCIATION_TYPE.OPERATION.key])}`"
              style="cursor:pointer"
              class="main fas fa-exclamation ml-2"
              aria-hidden="true"
            />
          </b-col>
        </b-row>
      </div>
    </div>
  </div>
</template>

<script>
import { uniqBy, cloneDeep, isEmpty } from 'lodash'
import { ASSOCIATION_TYPE } from '@/api/resources/configuration/peopleManagement/AssociationTypes'
import { OPERATION_GROUP } from '@/api/resources/configuration/peopleManagement/OperationGroups'
import OperationGroupFilter from '@/modules/configurationModule/detail/peopleManagement/OperationGroupFilter'
import ConfigurationSidepanelTabs from '@/modules/configurationModule/detail/ConfigurationSidepanelTabs'

export default {
    name: 'ConfigurationSidepanel',
    components: { ConfigurationSidepanelTabs, OperationGroupFilter },
    props: {
        sidepanelTitle: String,
        association: String,
        icon: String,
        list: { type: Array, default: () => { return [] } },
        associationsList: { type: Object },
        hasTabs: { type: Boolean, default: false },
        editDisabled: { type: Boolean, default: false },
        showCenterFilter: { type: Boolean, default: false },
        showEntitiesAssociationFilter: { type: Boolean, default: false },
        showOperationGroupFilter: { type: Boolean, default: false },
        associationType: { type: String, default: '' },
        associationTypes: { type: Array, default: () => { return [] } },
        showOperationTooltip: { type: Boolean, default: true }
    },
    data() {
        return {
            internalList: [],
            allLabsResponsibilities: [],
            allLabsRoles: [],
            labSelected: {},
            groupFilter: null
        }
    },
    computed: {
        filteredInternalList() {
            const list = this.internalList
            return list.filter(association => {
                if (this.groupFilter) {
                    if (this.groupFilter.value === this.OPERATION_GROUP.ACCESS.value) {
                        return association[this.ASSOCIATION_TYPE.OPERATION.key].type === this.OPERATION_GROUP.ACCESS.value
                    } else {
                        return this.getOperationGroup(association[this.ASSOCIATION_TYPE.OPERATION.key]).value === this.groupFilter.value
                    }
                } else {
                    return list
                }
            })
        },
        ASSOCIATION_TYPE() {
            return ASSOCIATION_TYPE
        },
        OPERATION_GROUP() {
            return OPERATION_GROUP
        },
        laboratories() {
            let assignedLaboratories = []
            if (this.list.length) {
                assignedLaboratories = uniqBy(this.list, 'laboratory.labId').map(item => item.laboratory)
                assignedLaboratories.unshift({ labName: this.$t('configuration.elementTitle.allCenters'), all: true })
            }
            return assignedLaboratories
        }
    },
    watch: {
        list() {
            this.internalList = cloneDeep(uniqBy(this.list, `${this.associationType}.id`))
            if (this.showCenterFilter) {
                if (this.internalList.length) {
                    let allLabsResponsibilities = []
                    this.labSelected = this.laboratories.find(lab => lab.all)
                    this.internalList.forEach(association => {
                        const responsibilityParsed = this.mapAssociation(association)
                        this.setAssociationLaboratories(responsibilityParsed, this.list, this.associationType)
                        allLabsResponsibilities.push(responsibilityParsed)
                    })
                    this.allLabsResponsibilities = allLabsResponsibilities.filter(responsibility => responsibility.laboratories.find(lab => lab.assigned && lab.all))
                    this.internalList = cloneDeep(this.allLabsResponsibilities)
                }
            }
        },
        labSelected() {
            if (this.labSelected && this.labSelected.labId) {
                this.internalList = uniqBy(this.list.filter(item => item.laboratory.labId === this.labSelected.labId), 'responsibility.id')
            } else {
                this.internalList = cloneDeep(this.allLabsResponsibilities)
            }
        },
        groupFilter() {
            if (this.hasTabs) this.$root.$emit('bv::hide::popover', 'tabs-operation-group-pop')
            else this.$root.$emit('bv::hide::popover', 'operation-group-pop')
        }
    },
    methods: {
        getAssociationName(association) {
            return association.translatedName ? association.translatedName : association.name
        },
        getOperationAssociatedRoles(operation) {
            let textHtml = ''
            const list = this.associationsList ? this.associationsList[ASSOCIATION_TYPE.OPERATION.key] : this.internalList
            const associatedRoles = list.filter(association => association.operation.id === operation.id).map(association => association.role)
            associatedRoles.forEach(associatedRole => {
                if (associatedRole) textHtml += `<p>${associatedRole.name}</p>`
            })
            return textHtml
        },
        isEmpty(value) {
            return isEmpty(value)
        },
        closeConfigurationSidepanel() {
            this.$emit('closeConfigurationSidepanel')
        },
        getOperationGroup(associationOperation) {
            let operationGroup = ''
            let regExp = ''
            for (let operation in this.OPERATION_GROUP) {
                if (this.OPERATION_GROUP[operation].groups) {
                    this.OPERATION_GROUP[operation].groups.forEach(group => {
                        regExp = new RegExp('^' + associationOperation.group)
                        if (regExp.test(group)) operationGroup = this.OPERATION_GROUP[operation]
                    })
                }
            }
            return operationGroup
        },
        mapAssociation(association) {
            let parsedAssociation = cloneDeep(association)
            parsedAssociation.laboratories = this.laboratories.map(lab => {
                let parsedLab = {}
                parsedLab.labId = lab ? lab.labId : null
                parsedLab.assigned = false
                if (lab && lab.all) parsedLab.all = lab.all
                return parsedLab
            })
            return parsedAssociation
        },
        setAssociationLaboratories(associationParsed, associations, associationType) {
            associationParsed.laboratories.forEach(lab => {
                associations.forEach(association => {
                    if (association[associationType].id === associationParsed[associationType].id && lab.labId === association.laboratory.labId) {
                        lab.userappRoleId = association.id
                        lab.assigned = true
                    }
                })
            })
            const labsAssigned = associationParsed.laboratories.filter(lab => lab.assigned).length
            let allLabs = associationParsed.laboratories.find(lab => lab.all)
            if (labsAssigned === this.laboratories.length - 1) {
                allLabs.assigned = true
            }
        }
    }
}
</script>

<style lang="scss">
    .configuration-sidepanel .title {
        font-size: font-size(big);
        color: vt-color(primary);
    }

    .configuration-sidepanel .tech-row {
        border-bottom: 1px solid rgb(209, 209, 209);
        font-size: font-size(small);
    }

    .configuration-sidepanel .scroll-area{
        height: calc(100vh - 220px);
        overflow-y: auto;
        overflow-x: hidden;
    }

    @media (max-width: 1300px) {
        .configuration-sidepanel .title {
            font-size: font-size(normal);
            color: vt-color(primary);
        }

        .configuration-sidepanel .scroll-area{
            height: calc(100vh - 206px);
            overflow-y: auto;
            overflow-x: hidden;
        }
    }
</style>

<style lang="scss">
    .configuration-sidepanel .vt-tabs .nav-tabs .nav-link.active,
    .vt-tabs--small .nav-tabs .nav-link.active {
        border-bottom: 2px solid vt-color(primary);
    }

    .configuration-sidepanel .vt-tabs .nav-tabs .nav-link,
    .vt-tabs--small .nav-tabs .nav-link {
        border-bottom: 2px solid transparent;
    }

    .configuration-sidepanel .vt-tabs .nav-tabs .nav-link,
    .vt-tabs--small .nav-tabs .nav-link i {
        font-size: font-size(very-normal);
    }
</style>
